// Pure helpers for parked contacts (2026-10-02, Lab-only behind
// PARKING_ENABLED), kept out of the components so node --test can cover them.
import { toMinutes } from './scheduleStatus.js'

export const PARKABLE_STATUSES = ['open', 'pending', 'escalated']
export const PARKABLE_CHANNELS = ['ebay', 'm365_email', 'task']

// m:ss under an hour, h:mm:ss from an hour — the Activity Log's own
// "Handle time" format, extended for long parks.
export function formatParkedDuration(ms) {
  const total = Math.max(0, Math.floor((ms ?? 0) / 1000))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const pad = (n) => String(n).padStart(2, '0')
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`
}

// May this viewer park this contact? Only its current owner, on eBay or M365
// email, while it is open, pending or escalated (the server enforces the same).
export function canPark({ parkingEnabled, conversation, isOwner }) {
  return Boolean(parkingEnabled && isOwner && conversation
    && PARKABLE_CHANNELS.includes(conversation.channel)
    && PARKABLE_STATUSES.includes(conversation.status))
}

// The most recent park on this contact (open or not), for the note shown
// beside the Park button.
export function latestPark(parks) {
  if (!parks?.length) return null
  return [...parks].sort((a, b) => new Date(b.parkedAt) - new Date(a.parkedAt))[0]
}

// "Parked by {name} · {timestamp}: {note}", "no note" when blank.
export function parkNoteLine(park, fmt = (t) => new Date(t).toLocaleString()) {
  if (!park) return null
  return `Parked by ${park.agentDisplayName ?? park.agent} · ${fmt(park.parkedAt)}: ${park.note?.trim() ? park.note : 'no note'}`
}

// The Parked view's customer column: the M365 customer email, or the eBay
// buyer ID — the same fields the conversation header shows.
export function parkedRowCustomer(c) {
  if (!c) return null
  return c.channel === 'ebay' ? (c.ebayBuyerId ?? null) : (c.customerEmail ?? null)
}

// Parks newest first, then each one's unpark, as Activity Log entries in time
// order among the suggestion entries: { kind: 'park' | 'unpark', at, park }.
export function parkActivityEntries(parks) {
  const out = []
  for (const p of parks ?? []) {
    out.push({ kind: 'park', at: p.parkedAt, park: p })
    if (p.unparkedAt) out.push({ kind: 'unpark', at: p.unparkedAt, park: p })
  }
  return out
}

// "resumed" / "taken over by X" / "returned to queue by X" / "closed" /
// "automatically re-queued".
export function unparkReasonText(park) {
  switch (park?.unparkReason) {
    case 'auto_requeued': return 'automatically re-queued'
    case 'resumed': return 'resumed'
    case 'taken_over': return `taken over by ${park.unparkedBy ?? 'someone'}`
    case 'returned_to_queue': return `returned to queue by ${park.unparkedBy ?? 'someone'}`
    case 'conversation_closed': return 'closed'
    default: return park?.unparkReason ?? ''
  }
}

// How long one park lasted (an open one runs to now).
export function parkDurationMs(park, nowMs = Date.now()) {
  if (!park) return 0
  const end = park.unparkedAt ? new Date(park.unparkedAt).getTime() : nowMs
  return Math.max(0, end - new Date(park.parkedAt).getTime())
}

// The admin "Parked Contacts Alert" threshold: 1 hour (back from 2 hours,
// 2026-10-08). The server's GET /api/parks returns its own alertMinutes and
// count; this is only the fallback when a response lacks one.
export const PARK_ALERT_MINUTES = 60

// Whether one park is past the alert threshold: open and parked longer than
// `minutes`. The admin alert's count and the Parked page's red rows
// (2026-10-08) both use this, so they always agree.
export function isParkOverdue(park, minutes = PARK_ALERT_MINUTES, nowMs = Date.now()) {
  return Boolean(park) && !park.unparkedAt && nowMs - new Date(park.parkedAt).getTime() > minutes * 60000
}

// "Expand"/badge counts and the admin alert, from the open parks.
export function overdueParkCount(parks, minutes = PARK_ALERT_MINUTES, nowMs = Date.now()) {
  return (parks ?? []).filter((p) => isParkOverdue(p, minutes, nowMs)).length
}

// --- The Parked page's order and admin filters (2026-10-08) ---

// Longest parked first (oldest parkedAt), ties by conversation number. Both
// sections use it.
export function sortParksLongestFirst(parks) {
  const num = (p) => Number(p?.conversation?.convNumber ?? Number.MAX_SAFE_INTEGER)
  return [...(parks ?? [])].sort((a, b) => (new Date(a.parkedAt).getTime() - new Date(b.parkedAt).getTime()) || (num(a) - num(b)))
}

const parkAgent = (p) => p?.agent ?? null
const parkCompany = (p) => p?.conversation?.companyCode ?? null
const parkClient = (p) => p?.conversation?.client ?? p?.conversation?.channel ?? null

// The filter options present in these rows: agents (value + display name),
// companies, clients, each sorted.
export function parkFilterOptions(parks) {
  const agents = new Map()
  const companies = new Set()
  const clients = new Set()
  for (const p of parks ?? []) {
    if (parkAgent(p)) agents.set(parkAgent(p), p.agentDisplayName ?? parkAgent(p))
    if (parkCompany(p)) companies.add(parkCompany(p))
    if (parkClient(p)) clients.add(parkClient(p))
  }
  return {
    agents: [...agents].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label)),
    companies: [...companies].sort(),
    clients: [...clients].sort(),
  }
}

export const EMPTY_PARK_FILTERS = Object.freeze({ agents: [], companies: [], clients: [] })

// Agent AND company AND client; an empty selection means all.
export function parkMatchesFilters(park, filters = EMPTY_PARK_FILTERS) {
  const inSel = (sel, v) => !sel?.length || sel.includes(v)
  return inSel(filters.agents, parkAgent(park)) && inSel(filters.companies, parkCompany(park)) && inSel(filters.clients, parkClient(park))
}

// Drops a selected value whose parks have all ended.
export function prunedParkFilters(filters, options) {
  const agentValues = options.agents.map((a) => a.value)
  return {
    agents: (filters?.agents ?? []).filter((v) => agentValues.includes(v)),
    companies: (filters?.companies ?? []).filter((v) => options.companies.includes(v)),
    clients: (filters?.clients ?? []).filter((v) => options.clients.includes(v)),
  }
}

export function hasParkFilters(filters) {
  return Boolean(filters?.agents?.length || filters?.companies?.length || filters?.clients?.length)
}

// Remembered per admin; every read and write guarded.
export function parkFiltersKey(agent) {
  return `cxhub:parked-filters:${agent ?? ''}`
}
export function readParkFilters(agent, storage = globalThis.localStorage) {
  try {
    const raw = JSON.parse(storage?.getItem(parkFiltersKey(agent)) ?? 'null')
    if (!raw || typeof raw !== 'object') return { ...EMPTY_PARK_FILTERS }
    const list = (v) => (Array.isArray(v) ? v.filter((x) => typeof x === 'string') : [])
    return { agents: list(raw.agents), companies: list(raw.companies), clients: list(raw.clients) }
  } catch {
    return { ...EMPTY_PARK_FILTERS }
  }
}
export function writeParkFilters(agent, filters, storage = globalThis.localStorage) {
  try { storage?.setItem(parkFiltersKey(agent), JSON.stringify(filters)) } catch { /* storage unavailable */ }
}

// --- End-of-shift reminder (2026-10-07, Lab-only behind PARKING_ENABLED) ---
// 30 minutes before the agent's shift ends, an agent with parked contacts gets
// an in-app reminder, once per shift. Client-side only: the schedule is
// Schedule Hub's own (GET /api/schedule/today, read in its own timezone) and
// the count is the agent's own open parks.

export const SHIFT_REMINDER_LEAD_MINUTES = 30

// Due when parking is on, the schedule has a shift end on a working day, now
// is within the last 30 minutes before it, the agent has parked contacts, and
// it hasn't been shown for this shift yet. nowHHMM is in the schedule's own
// timezone. Overnight shifts are out of scope, as in computeScheduleStatus().
export function shiftEndReminderDue({ parkingEnabled, schedule, nowHHMM, parkCount, alreadyShown }) {
  if (!parkingEnabled || !schedule || schedule.day_off) return false
  if (!schedule.shift_start || !schedule.shift_end) return false
  const end = toMinutes(schedule.shift_end)
  const now = toMinutes(nowHHMM)
  if (end == null || now == null) return false
  if (!(parkCount > 0) || alreadyShown) return false
  return now >= end - SHIFT_REMINDER_LEAD_MINUTES && now < end
}

// Today's date (YYYY-MM-DD) in a timezone, for the once-per-shift key.
export function dateInZone(timezone, now = new Date()) {
  return new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now)
}

// One key per agent, date and shift end.
export function shiftReminderKey({ agent, date, shiftEnd }) {
  return `cxhub:shift-reminder-seen:${agent ?? ''}:${date}:${shiftEnd}`
}

// Every read and write is guarded: when storage is unavailable the reminder
// still works, it just can't remember being seen.
export function readFlag(key, storage = globalThis.localStorage) {
  try { return storage?.getItem(key) === '1' } catch { return false }
}
export function writeFlag(key, storage = globalThis.localStorage) {
  try { storage?.setItem(key, '1') } catch { /* storage unavailable */ }
}

// The Lab-only "Test shift end" override: Schedule Hub is off in the Labs, so
// a synthetic schedule stands in for the reminder logic only. Ignored unless
// the app reports DEPLOY_ENV=lab; never touches the header widget.
export const LAB_SHIFT_END_KEY = 'cxhub:lab-test-shift-end'
const HHMM_RE = /^([01]\d|2[0-3]):[0-5]\d$/

export function labOverrideSchedule({ deployEnv, overrideHHMM, timezone }) {
  if (deployEnv !== 'lab' || !HHMM_RE.test(overrideHHMM ?? '')) return null
  return { day_off: false, shift_start: '00:00', shift_end: overrideHHMM, timezone }
}
export function readLabShiftEnd(storage = globalThis.localStorage) {
  try { return storage?.getItem(LAB_SHIFT_END_KEY) || '' } catch { return '' }
}
export function writeLabShiftEnd(value, storage = globalThis.localStorage) {
  try {
    if (value) storage?.setItem(LAB_SHIFT_END_KEY, value)
    else storage?.removeItem(LAB_SHIFT_END_KEY)
  } catch { /* storage unavailable */ }
}

// --- Automatic re-queue (2026-10-07) -------------------------------------------
// A park open 3 hours is ended by the server (services/park-auto-requeue.js)
// and its agent gets a notice.

// The Activity Log line for an auto-re-queued park.
export function autoRequeueLine(park, fmt = (t) => new Date(t).toLocaleString()) {
  return `Automatically re-queued after ${formatParkedDuration(parkDurationMs(park))} parked · ${fmt(park.unparkedAt)}`
}

// Why Re-Park is unavailable for a notice, or null when it is available. The
// server refuses the same cases.
export function reparkBlockedReason(notice) {
  const c = notice?.conversation
  if (!c) return 'This contact is no longer available'
  if (c.status === 'closed') return 'This contact was closed'
  if (c.currentOwner && c.currentOwner !== notice.agent) return 'Another agent has this contact now'
  if (!PARKABLE_CHANNELS.includes(c.channel) || !PARKABLE_STATUSES.includes(c.status)) return "This contact can't be parked now"
  return null
}

// The X in the conversation header: for the owner it releases the claim, so
// the contact goes back to the queue; for anyone else it only closes the view.
export function closeButtonTooltip(isOwner) {
  return isOwner ? 'This will return this contact to the queue' : 'Stop viewing this contact'
}

// The server's Re-Park / acknowledge refusals, as the notice shows them.
const REPARK_ERROR_TEXT = {
  owned_by_other: 'Another agent has this contact now',
  closed: 'This contact was closed',
  wrong_status: "This contact can't be parked now",
  wrong_channel: "This contact can't be parked now",
  already_parked: 'This contact is already parked',
  already_acknowledged: 'This notice was already answered',
  not_owner: 'This notice belongs to another agent',
  not_found: 'This notice is no longer available',
}
export function reparkErrorText(code) {
  return REPARK_ERROR_TEXT[code] ?? null
}

// The end-of-shift reminder's count, singular or plural (2026-10-08).
export function parkedContactsText(count) {
  return `${count} parked contact${count === 1 ? '' : 's'}`
}

// The Parked page's own deep link (2026-10-08): /parked opens the app on the
// Parked page, only when parking is on (otherwise it falls back to '/' like
// any unknown path). The reminder's "View Parked Contacts" opens it in a new
// tab so the agent's current tab keeps its open contact.
export const PARKED_PATH = '/parked'
export function isParkedPath(pathname) {
  return /^\/parked\/?$/.test(pathname ?? '')
}
export function navPageForParkedPath(pathname, parkingEnabled) {
  return parkingEnabled && isParkedPath(pathname) ? 'parked' : null
}
